import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Image blocks that read a variant metafield (e.g. ritual_image, ingredient_image).
 * Variant picker remorphs only the picker on same-product changes, so these images
 * stay stale unless they swap themselves from the fetched page HTML.
 *
 * @extends {Component}
 */
class VariantImage extends Component {
  /** @type {AbortController | null} */
  #variantUpdateController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#variantUpdateController?.abort();
    this.#variantUpdateController = new AbortController();

    // Images may live in a different section than the picker.
    document.addEventListener(ThemeEvents.variantUpdate, this.#updateImage, {
      signal: this.#variantUpdateController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#variantUpdateController?.abort();
    this.#variantUpdateController = null;
  }

  /**
   * Dialog, featured product, or main — whichever hosts this node or event.
   * Featured-product events are dispatched on the wrapping section, so look inside it.
   * @param {Element} element
   * @returns {Element | null}
   */
  #getSurface(element) {
    const dialog = element.closest('dialog');
    if (dialog) return dialog;

    const featured = element.closest('featured-product-information');
    if (featured) return featured;

    if (element instanceof HTMLElement && element.matches('.shopify-section')) {
      const featuredChild = element.querySelector('featured-product-information');
      if (featuredChild) return featuredChild;
    }

    return element.closest('main');
  }

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  #eventBelongsToImage(event) {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('product-card')) return false;

    const imageSurface = this.#getSurface(this);
    const eventSurface = this.#getSurface(target);
    return Boolean(imageSurface && eventSurface && imageSurface === eventSurface);
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #updateImage = (event) => {
    if (!this.#eventBelongsToImage(event)) return;
    if (event.detail.data.morphElementSelector === 'main') return;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (this.dataset.productId && event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const blockId = this.dataset.blockId;
    if (!blockId) return;

    const next = event.detail.data.html.querySelector(`variant-image[data-block-id="${CSS.escape(blockId)}"]`);
    if (!next || next.isEqualNode(this)) return;

    morph(this, next, { childrenOnly: true });
  };
}

if (!customElements.get('variant-image')) {
  customElements.define('variant-image', VariantImage);
}
